import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';
import { WeightDiagram } from '../../components/Diagrams';

export default function TrailerRegler() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Kørekort og vægt", path: "/trailer-regler" }
  ];
  const tocItems = [
    { id: "hovedregler", title: "Hovedreglerne for kørsel med trailer" },
    { id: "koerekort", title: "Kørekortkategorier: B, B+ og BE" },
    { id: "hastighed", title: "Hastighedsgrænser i Danmark" },
    { id: "vaegt-og-laesning", title: "Vægtgrænser og korrekt læsning" },
    { id: "syn-og-registrering", title: "Syn, nummerplade og afgift" }
  ];

  const faq = [
    {
      q: "Hvad må man altid køre med på et almindeligt B-kørekort?",
      a: "Med et almindeligt kategori B-kørekort må du altid trække en trailer med en tilladt totalvægt på op til 750 kg, så længe bilens registreringsattest tillader trailerens vægt. Vogntogets samlede tilladte vægt må i dette tilfælde nå helt op på 4.250 kg."
    },
    {
      q: "Hvor hurtigt må jeg køre med trailer på motorvejen?",
      a: "Standardhastighedsgrænsen med trailer på motorvej i Danmark er 80 km/t. Du må kun køre 100 km/t på motorvej, hvis traileren er særskilt godkendt til dansk Tempo 100 og forsynet med det officielle Tempo 100-mærke."
    },
    {
      q: "Skal traileren have sin egen ansvarsforsikring?",
      a: "Nej, lovpligtig ansvarsforsikring dækkes altid automatisk af den bil, der trækker traileren. Hvis du ønsker dækning mod skader på selve traileren eller mod tyveri, skal du derimod tegne en særskilt kaskoforsikring."
    },
    {
      q: "Hvor finder jeg de tilladte vægte for min bil og trailer?",
      a: "Du finder bilens maksimalt tilladte påhængsvognsvægt i feltet for teknisk tilladt totalvægt af påhængskøretøj på bilens registreringsattest. Trailerens tilladte totalvægt fremgår af trailerens registreringsattest og det pånittede vægtskilt på trailerens chassis."
    }
  ];

  const sources = [
    { title: "Færdselsloven (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" },
    { title: "Kørekortbekendtgørelsen (BEK nr. 1243 af 28/08/2020)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2020/1243" },
    { title: "Færdselsstyrelsen: Kørekort til påhængskøretøj til bil", source: "fstyr.dk", url: "https://fstyr.dk/privat/koerekort/udvidelse-af-koerekort/koerekort-til-paahaengskoeretoej-til-bil" },
    { title: "Motorstyrelsen: Regler for trailer og påhængsvogn", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/trailer" }
  ];

  return (
    <>
      <SEO 
        title="Trailerregler i Danmark — Overblik over lovkrav og vægt" 
        description="Komplet overblik over trailerregler i Danmark: kørekort (B, B+, BE), vægtgrænser, hastigheder, syn, nummerplader og lastsikring." 
        pathname="/trailer-regler"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Trailerregler i Danmark: Det samlede overblik
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Kørsel med trailer i Danmark er reguleret af færdselsloven, kørekortbekendtgørelsen og detailforskrifterne for køretøjer. Som fører har du altid det fulde juridiske ansvar for, at sammenkoblingen er lovlig, at dit kørekort dækker vogntogets vægt, og at traileren er læsset og fastgjort korrekt.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          De to mest afgørende faktorer er kørekortkategorien og vægtgrænserne. Med et almindeligt B-kørekort må du altid trække en trailer med en tilladt totalvægt på op til 750 kg. Er traileren tungere end 750 kg, må bilens og trailerens tilladte totalvægte tilsammen højst udgøre 3.500 kg. På alle offentlige veje gælder en generel hastighedsgrænse på 80 km/t uden for tættere bebygget område.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Hurtigt overblik">
          <p className="m-0">
            Før du kører ud, skal du kontrollere tre ting: 1) Har du det rette kørekort til vogntogets samlede tilladte totalvægt? 2) Må trækbilen trække trailerens faktiske vægt ifølge registreringsattesten? 3) Er kugletrykket inden for det tilladte spænd (typisk 50–75 kg)?
          </p>
        </Callout>

        <h2 id="hovedregler" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hovedreglerne for kørsel med trailer
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du spænder en trailer på krogen, forvandles din personbil eller varebil til et vogntog. Reglerne for et vogntog adskiller sig markant fra solokørsel med hensyn til hastighed, overhaling og bremselængder.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          I færdselsmæssig sammenhæng skelnes der skarpt mellem to former for vægtangivelser: den <em>tilladte totalvægt</em> (det tal, der er trykt på registreringsattesten) og den <em>faktiske totalvægt</em> (hvad vogntoget reelt vejer på en kontrolvægt). Kørekortets gyldighed afgøres udelukkende af de tilladte totalvægte, mens bilens trækevne afgøres af trailerens faktiske vægt.
        </p>

        <WeightDiagram caption="Figur 1: Opdeling af vægte mellem trækbil, koblingspunkt (kugletryk) og trailerens tilladte totalvægt." />

        <h2 id="koerekort" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kørekortkategorier: B, B+ og BE
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvilket kørekort du skal have for at føre en trailer, afhænger af vægtene på henholdsvis bil og trailer. Reglerne fastsættes i bekendtgørelse om kørekort:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Kategori</th>
                <th className="py-2.5 pr-4 font-semibold">Trailer totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Vogntogets max totalvægt</th>
                <th className="py-2.5 font-semibold">Typisk anvendelse</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Kategori B</td>
                <td className="py-3 pr-4">Højst 750 kg</td>
                <td className="py-3 pr-4">Op til 4.250 kg</td>
                <td className="py-3 text-[#5C636B]">Standard have- og flyttetrailer</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Kategori B</td>
                <td className="py-3 pr-4">Over 750 kg</td>
                <td className="py-3 pr-4">Højst 3.500 kg</td>
                <td className="py-3 text-[#5C636B]">Mellemstor trailer eller let campingvogn</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Kategori B+</td>
                <td className="py-3 pr-4">Over 750 kg</td>
                <td className="py-3 pr-4">Højst 4.250 kg</td>
                <td className="py-3 text-[#5C636B]">Tung campingvogn eller hestetrailer</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Kategori BE</td>
                <td className="py-3 pr-4">Op til 3.500 kg</td>
                <td className="py-3 pr-4">Højst 7.000 kg</td>
                <td className="py-3 text-[#5C636B]">Store erhvervstrailere, autotrailere, maskintrailere</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Du kan fordybe dig i de præcise beregninger i vores dybdegående guide om <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekort og trailer</Link> eller læse mere om <Link to="/hvor-meget-maa-man-traekke" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">hvor meget du må trække</Link>.
        </p>

        <h2 id="hastighed" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hastighedsgrænser i Danmark
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark er hastighedsgrænserne for vogntog ensrettet på de fleste overordnede vejtyper. Det betyder, at du uden særlig godkendelse højst må køre 80 km/t på landeveje, motortrafikveje og motorveje.
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Vejtype</th>
                <th className="py-2.5 pr-4 font-semibold">Standard trailer</th>
                <th className="py-2.5 pr-4 font-semibold">Tempo 100 trailer</th>
                <th className="py-2.5 font-semibold">Bemærkning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Byzone</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 text-[#5C636B]">Eller lavere lokal skiltning</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Landevej</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">Generel landevejsgrænse</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Motortrafikvej</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">Hævet fra 70 til 80 km/t i 2020</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Motorvej</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">100 km/t</td>
                <td className="py-3 text-[#5C636B]">Kræver dansk Tempo 100 attest</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Ønsker du at køre op til 100 km/t på de danske motorveje, skal traileren synes og registreres til ordningen. Læs alt om kravene i artiklen om <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Tempo 100 godkendelse</Link> samt vores artikel om <Link to="/trailer-hastighed" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer hastighedsgrænser</Link>.
        </p>

        <h2 id="vaegt-og-laesning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Vægtgrænser og korrekt læsning
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Overlæs og forkert vægtfordeling er blandt de hyppigste årsager til ulykker med påhængskøretøjer. Hvis godset placeres for langt bagud på traileren, bliver kugletrykket for lavt, hvilket skaber en faretruende slingren ved høj fart.
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Placér det tungeste gods over akslen:</strong> Tyngdepunktet skal ligge lige foran trailerens hjulaksel.</li>
          <li><strong>Overhold bilens kugletryk:</strong> Trykket på trækkrogen skal typisk være mellem 50 og 75 kg. Se den nøjagtige S-værdi på bilens anhængertræk i vores guide om <Link to="/kugletryk" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kugletryk</Link>.</li>
          <li><strong>Sikring af last:</strong> Løst gods som haveaffald eller sand skal altid dækkes med et trailernet eller en presenning. Se <Link to="/lastsikring" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">regler for lastsikring</Link>.</li>
          <li><strong>Lasteevne:</strong> Træk egenvægten fra den tilladte totalvægt for at finde den maksimale tilladte nyttelast. Læs mere om <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">totalvægt og lasteevne</Link>.</li>
        </ul>

        <h2 id="syn-og-registrering" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Syn, nummerplade og afgift
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark skal en almindelig trailer ikke til faste periodiske syn som personbiler. Den skal som udgangspunkt kun synes i forbindelse med ejerskifte, hvis den er ældre end 5 år, medmindre den er godkendt til Tempo 100.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Der betales ingen registreringsafgift ved køb af trailer i Danmark. Er trailerens tilladte totalvægt på 500 kg eller derunder, opkræves der heller ingen løbende vægtafgift. Vejer traileren mere end 500 kg, betales der en årlig vægtafgift opkrævet af Motorstyrelsen.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om de praktiske detaljer i vores guides om <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link>, <Link to="/trailer-nummerplade" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">nummerplader</Link> og <Link to="/trailer-vaegtafgift" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">vægtafgift på trailer</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
